import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";

const DAY_MS = 24 * 60 * 60 * 1000;

const TrialBanner: React.FC = () => {
  const { subscription, loading } = useBilling();
  const navigate = useNavigate();

  const trialInfo = useMemo(() => {
    if (!subscription?.trialEnd) return null;
    const trialEnd = new Date(subscription.trialEnd);
    const remainingMs = trialEnd.getTime() - Date.now();
    const daysRemaining = Math.max(0, Math.ceil(remainingMs / DAY_MS));
    const isExpired = subscription.status === "EXPIRED" || daysRemaining <= 0;
    return { daysRemaining, isExpired };
  }, [subscription]);

  if (loading || !subscription || !trialInfo) return null;
  if (subscription.status !== "TRIALING" && !trialInfo.isExpired) return null;

  const label = trialInfo.isExpired
    ? "Your free trial has ended"
    : `Free trial · ${trialInfo.daysRemaining} day${trialInfo.daysRemaining === 1 ? "" : "s"} remaining`;
  const message = trialInfo.isExpired
    ? "Upgrade to keep recruiting without interruption."
    : "Upgrade anytime to keep full access after your trial.";

  return (
    <div
      className={`border-b px-4 py-3 text-sm ${
        trialInfo.isExpired ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"
      }`}
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div className="flex items-start gap-3">
          <span
            className={`mt-0.5 flex size-8 items-center justify-center rounded-full ${
              trialInfo.isExpired ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"
            }`}
          >
            <Sparkles className="size-4" />
          </span>
          <div>
            <p className="font-semibold text-gray-900">{label}</p>
            <p className="text-gray-600">{message}</p>
          </div>
        </div>
        <button
          type="button"
          className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition ${
            trialInfo.isExpired ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800"
          }`}
          onClick={() => navigate("/pricing")}
        >
          Upgrade now
        </button>
      </div>
    </div>
  );
};

export default TrialBanner;
